import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  DialogActions,
  Button,
  Box,
  List,
  ListItem,
} from "@mui/material";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import colors from "../../../../../../Constants/colors";
import { Clear } from "@mui/icons-material";
import { dialogStyles } from "../../../../../../Constants/dialogStyles";
import theme, { createLLMTypography } from "../../../../../../Constants/theme";
import {
  primaryButtonStyles,
  textButtonStyles,
} from "../../../../../../Constants/buttonStyles";

const options = [
  "Multi phrasing",
  "HyDE",
  "Deconstruction",
  "Expansion",
  "Iterative Deconstruction",
];

const choicesMapping = {
  "Multi phrasing": "multi_query",
  HyDE: "hyde",
  Deconstruction: "complex_to_simple",
  Expansion: "query_expansion",
  "Iterative Deconstruction": "iterative_deconstruction",
};

function DraggableItem({ id, name, handleRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition: "transform 250ms ease",
        padding: "8px",
        border: "1px solid #ddd",
        marginBottom: "4px",
        backgroundColor: "white",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "200px",
      }}
    >
      <DragIndicatorIcon
        {...listeners}
        {...attributes}
        style={{ cursor: "grab", color: theme.main.extremeBlack }}
      />
      <span
        style={{
          color: theme.main.extremeBlack,
          fontSize: createLLMTypography.fontSizes.sectionText2,
        }}
      >
        {name}
      </span>
      <IconButton
        onClick={() => handleRemove(id)}
        size="small"
        style={{ marginLeft: "auto", color: theme.main.extremeBlack }}
      >
        ×
      </IconButton>
    </div>
  );
}

function OptionItem({ option, handleAddItem }) {
  return (
    <div
      onClick={() => {
        handleAddItem(option);
      }}
      style={{
        padding: "8px",
        border: "1px solid #ddd",
        marginBottom: "4px",
        cursor: "pointer",
        backgroundColor: "#f7f7f7",
        width: "200px",
        textAlign: "center",
        color: theme.main.extremeBlack,
        fontSize: createLLMTypography.fontSizes.sectionText2,
      }}
    >
      {option}
    </div>
  );
}

export default function QueryChainingDialog({
  dialogOpen,
  setDialogOpen,
  chainingPromptsQT,
  setChainingPromptsQT,
  setSequenceHistory,
  sequenceHistory,
  selectedChainItems,
  setSelectedChainItems,
  botData,
  setBotData,
  nodeId,
}) {
  const [draggedArea, setDraggedArea] = useState([]);
  const [draggingItem, setDraggingItem] = useState(null);
  const [existingIndex, setExistingIndex] = useState(-1);
  const [infoText, setInfoText] = useState("");

  // Initialize draggedArea if chainingPromptsQT has sequences saved
  useEffect(() => {
    if (
      dialogOpen &&
      typeof chainingPromptsQT === "string" &&
      chainingPromptsQT.length > 0
    ) {
      const index = sequenceHistory.indexOf(chainingPromptsQT);
      setExistingIndex(index); // Store the index of chainingPromptsQT in sequenceHistory, or -1 if it doesn't exist

      const initialItems = chainingPromptsQT.split("→").map((choice) => {
        const name = Object.keys(choicesMapping).find(
          (key) => choicesMapping[key] === choice
        );
        return { id: `${name}-${Date.now()}`, name };
      });
      setDraggedArea(initialItems);
      setInfoText("");
    } else if (dialogOpen) {
      setExistingIndex(-1);
      setChainingPromptsQT("");
      setDraggedArea([]);

      setInfoText("");
    }
  }, [chainingPromptsQT, sequenceHistory, dialogOpen]);

  const handleAddItem = (item) => {
    if (draggedArea.length >= 4) {
      // Limit reached
      setInfoText("You can only add up to 4 query types.");
    } else if (
      (item === "Multi phrasing" ||
        item === "Deconstruction" ||
        item === "Iterative Deconstruction") &&
      draggedArea.some((entry) =>
        [
          "Multi phrasing",
          "Deconstruction",
          "Iterative Deconstruction",
        ].includes(entry.name)
      )
    ) {
      // Either "Multi phrasing" or "Deconstruction" already exists
      setInfoText(
        "You can only add one of 'Multi phrasing' or 'Deconstruction' or 'Iterative Deconstruction', and only once."
      );
    } else {
      // Valid item to add
      const data = { id: `${item}-${Date.now()}`, name: item };

      setDraggedArea((prev) => [...prev, data]);
      setInfoText(""); // Clear info text if item was successfully added
    }
  };

  const handleRemoveItem = (id) => {
    setDraggedArea((prev) => prev.filter((entry) => entry.id !== id));
    setInfoText("");
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const item = draggedArea.find((entry) => entry.id === active.id) || {
      id: active.id,
      name: active.id,
    };
    if (!draggedArea.some((entry) => entry.id === active.id)) {
      setDraggedArea((prev) => [...prev, item]);
    }
    setDraggingItem(item);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = draggedArea.findIndex((item) => item.id === active.id);
      const newIndex = draggedArea.findIndex((item) => item.id === over.id);
      setDraggedArea((items) => arrayMove(items, oldIndex, newIndex));
    }
    setDraggingItem(null);
  };

  const handleCancel = () => {
    setDraggedArea([]);
    setExistingIndex(-1); // Reset existingIndex on cancel
    setChainingPromptsQT("");
    setDialogOpen(false);
  };

  const handleSave = () => {
    const sequence = draggedArea
      .map((item) => choicesMapping[item.name])
      .join("→");

    if (existingIndex !== -1) {
      // If sequence exists, update it at the found index
      setSequenceHistory((prev) =>
        prev.map((item, index) => (index === existingIndex ? sequence : item))
      );
      // save to botdata as false

      setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            ...prevData.workflow[`${nodeId}`],

            query_preprocess_method: {
              ...prevData.workflow[nodeId].query_preprocess_method,
              [sequence]: false,
            },
          },
        },
      }));
      localStorage.setItem(
        `queryTransform_${nodeId}`,
        JSON.stringify({
          ...botData.workflow[nodeId].query_preprocess_method,
          [sequence]: false,
        })
      );

      // deselect checkbox
      // if (selectedChainItems?.includes(existingIndex)) {
      //   setSelectedChainItems((prevSelectedItems) =>
      //     prevSelectedItems.filter((item) => item !== existingIndex)
      //   );
      // }
    } else {
      // If sequence doesn't exist, add it to the array
      if (sequenceHistory.includes(sequence)) {
        alert("This sequence already exists. Please create a unique sequence."); // Notify user
        return; // Exit without saving
      }
      setSequenceHistory((prev) => [...prev, sequence]);

      setBotData((prevData) => {
        const existingQpm =
          prevData.workflow[nodeId].query_preprocess_method || {};
        const nextQpm = {
          ...existingQpm,
          [sequence]: false,
        };
        // persist it
        localStorage.setItem(
          `queryTransform_${nodeId}`,
          JSON.stringify(nextQpm)
        );

        return {
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [nodeId]: {
              ...prevData.workflow[nodeId],
              query_preprocess_method: nextQpm,
            },
          },
        };
      });
    }

    setChainingPromptsQT(sequence); // Save sequence as a string to chainingPromptsQT
    setDraggedArea([]);
    setExistingIndex(-1); // Reset existingIndex after save
    setDialogOpen(false);
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleCancel}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: dialogStyles.dialogRoot }}
    >
      <DialogTitle sx={dialogStyles.dialogTitle}>
        <Typography sx={dialogStyles.dialogTitleText}>
          Create Query Transformation Chaining
        </Typography>
        <Clear onClick={handleCancel} sx={dialogStyles.dialogCloseIcon} />
      </DialogTitle>
      <DialogContent sx={dialogStyles.dialogContent}>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            display: "flex",
          }}
        >
          <Typography
            component="div"
            sx={{
              color: theme.textColors.dark.primary,
              fontSize: createLLMTypography.fontSizes.sectionText1, // wrapper text size
              mb: 2,
            }}
            gutterBottom
          >
            {/* Heading uses sectionTitle */}
            <Typography
              component="div"
              sx={{
                fontWeight: theme.typography.fontWeight.bold,
                fontSize: createLLMTypography.fontSizes.sectionTitle,
                mb: 1,
                color: theme.textColors.dark.primary,
              }}
            >
              Instructions:
            </Typography>

            <List
              component="ul"
              sx={{
                p: 0,
                pl: "1.5em",
                "& > li": {
                  display: "list-item",
                },
              }}
            >
              <ListItem component="li" disableGutters>
                <Typography
                  sx={{
                    fontSize: createLLMTypography.fontSizes.sectionText2,
                    color: theme.textColors.dark.primary,
                  }}
                >
                  <strong>Build your sequence</strong> by clicking on options in
                  the left panel to add them to the Chaining Panel.
                </Typography>
              </ListItem>

              <ListItem component="li" disableGutters>
                <Typography
                  sx={{
                    fontSize: createLLMTypography.fontSizes.sectionText2,
                    color: theme.textColors.dark.primary,
                  }}
                >
                  Add two or more items to build a Query Transformation Chain.
                </Typography>
              </ListItem>

              <ListItem component="li" disableGutters>
                <Typography
                  sx={{
                    fontSize: createLLMTypography.fontSizes.sectionText2,
                    color: theme.textColors.dark.primary,
                  }}
                >
                  <strong>Limit:</strong> You can add up to{" "}
                  <strong>4 items</strong>. “Multi phrasing,” “Deconstruction”
                  and “Iterative Deconstruction” can each be added only once.
                </Typography>
              </ListItem>

              <ListItem component="li" disableGutters>
                <Typography
                  sx={{
                    fontSize: createLLMTypography.fontSizes.sectionText2,
                    color: theme.textColors.dark.primary,
                  }}
                >
                  <strong>Arrange order:</strong> Once added to the right panel,
                  you can drag items up or down to reorder them.
                </Typography>
              </ListItem>

              <ListItem component="li" disableGutters>
                <Typography
                  sx={{
                    fontSize: createLLMTypography.fontSizes.sectionText2,
                    color: theme.textColors.dark.primary,
                  }}
                >
                  <strong>Remove items:</strong> Click the “×” button on each
                  item to remove it from the sequence.
                </Typography>
              </ListItem>
            </List>
          </Typography>

          <Typography
            sx={{
              color: theme.state.warning.primary,
              fontSize: createLLMTypography.fontSizes.sectionText2,
              mt: 1,
            }}
          >
            {infoText}
          </Typography>

          <div
            style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}
          >
            {/* Options List within the DndContext */}
            <DndContext
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <div>
                <Typography
                  sx={{ fontSize: createLLMTypography.fontSizes.sectionText1 }}
                >
                  Options
                </Typography>
                <SortableContext items={options}>
                  {options.map((option) => (
                    <OptionItem
                      key={option}
                      option={option}
                      handleAddItem={handleAddItem}
                    />
                  ))}
                </SortableContext>
              </div>

              {/* Droppable Area */}
              <div>
                <Typography
                  sx={{ fontSize: createLLMTypography.fontSizes.sectionText1 }}
                >
                  Dragged Area
                </Typography>
                <SortableContext items={draggedArea.map((item) => item.id)}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {draggedArea.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <DraggableItem
                          key={item.id}
                          id={item.id}
                          name={item.name}
                          handleRemove={handleRemoveItem}
                        />
                        {index < draggedArea.length - 1 && (
                          <div
                            style={{
                              margin: "8px 0",
                              color: "#fff",
                              fontSize:
                                createLLMTypography.fontSizes.sectionText2,
                              textAlign: "center",
                            }}
                          >
                            ↓
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {draggingItem ? (
                    <div
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "200px",
                      }}
                    >
                      <DragIndicatorIcon />
                      <span>{draggingItem.name}</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </div>
            </DndContext>
          </div>
        </Box>
      </DialogContent>
      <DialogActions sx={dialogStyles.dialogActions}>
        <Button onClick={handleCancel} sx={textButtonStyles}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          sx={primaryButtonStyles}
          disabled={draggedArea?.length <= 1}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
